import { GameState, Scenario, Zone, OpponentPosition } from './types';

/**
 * Trapezoidal perspective renderer.
 *
 * The court is drawn as if the player is standing at the baseline looking
 * toward the opponent's end. Near side (bottom) is wide, far side (top)
 * is narrow — standard 1-point perspective.
 *
 * All court positions use normalized coords:
 *   rx: 0 = left sideline, 1 = right sideline
 *   ry: 0 = player baseline (bottom/near), 1 = opponent baseline (top/far)
 *
 * These are projected to canvas coords via perspectiveXY().
 */
export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private w = 0;
  private h = 0;

  // Perspective trapezoid corners (canvas coords)
  private cx = 0;           // horizontal center
  private nearY = 0;        // player baseline Y (bottom, wide)
  private farY = 0;         // opponent baseline Y (top, narrow)
  private nearHalfW = 0;    // half-width at near baseline
  private farHalfW = 0;     // half-width at far baseline

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
    this.w = canvas.width;
    this.h = canvas.height;
    this.calcLayout();
  }

  resize(w: number, h: number) {
    this.w = w;
    this.h = h;
    this.calcLayout();
  }

  private calcLayout() {
    const pad = Math.min(this.w, this.h) * 0.04;
    this.cx = this.w / 2;
    this.nearY = this.h * 0.92;
    this.farY = this.h * 0.10;
    // Near baseline is ~85% of canvas width, far is ~35%
    this.nearHalfW = (this.w - pad * 2) * 0.42;
    this.farHalfW = this.nearHalfW * 0.38;
  }

  // ---- Perspective projection ----

  /** Map normalized court position to canvas coords.
   *  rx: 0=left, 1=right.  ry: 0=near(player), 1=far(opponent). */
  private perspXY(rx: number, ry: number): { x: number; y: number } {
    // Non-linear depth compression: things farther away compress more
    const t = 1 - Math.pow(1 - ry, 0.75);
    const y = this.nearY + (this.farY - this.nearY) * t;
    const halfW = this.nearHalfW + (this.farHalfW - this.nearHalfW) * t;
    const x = this.cx - halfW + rx * halfW * 2;
    return { x, y };
  }

  /** Inverse: canvas coords to normalized court coords, or null if outside */
  private canvasToCourt(cx: number, cy: number): { rx: number; ry: number } | null {
    // Solve for ry first: cy = nearY + (farY - nearY) * t, t = f(ry)
    const totalH = this.farY - this.nearY;
    if (totalH === 0) return null;
    const t = (cy - this.nearY) / totalH;
    if (t < 0 || t > 1) return null;
    // Invert the non-linear mapping: t = 1 - (1-ry)^0.75 → ry = 1 - (1-t)^(1/0.75)
    const ry = 1 - Math.pow(1 - t, 1 / 0.75);
    if (ry < 0 || ry > 1) return null;

    const halfW = this.nearHalfW + (this.farHalfW - this.nearHalfW) * t;
    const leftX = this.cx - halfW;
    const rx = (cx - leftX) / (halfW * 2);
    if (rx < 0 || rx > 1) return null;

    return { rx, ry };
  }

  // ---- Zone geometry ----

  /** Get the 4 corners (trapezoid) of a target zone in canvas coords */
  getZoneCorners(zone: Zone): { tl: {x:number,y:number}; tr: {x:number,y:number}; bl: {x:number,y:number}; br: {x:number,y:number} } {
    // Zones are on opponent's half (ry 0.5 to 1.0)
    // 3 columns: cross(0-0.33), middle(0.33-0.67), line(0.67-1.0)
    // 2 rows: short(0.5-0.72), deep(0.72-1.0)
    const cols: Record<string, [number, number]> = {
      'cross':  [0.0, 0.33],
      'middle': [0.33, 0.67],
      'line':   [0.67, 1.0],
    };
    const rows: Record<string, [number, number]> = {
      'short': [0.50, 0.72],
      'deep':  [0.72, 1.0],
    };

    const [dir, depth] = zone.split('-') as [string, string];
    const [x0, x1] = cols[dir];
    const [y0, y1] = rows[depth];

    return {
      bl: this.perspXY(x0, y0),
      br: this.perspXY(x1, y0),
      tl: this.perspXY(x0, y1),
      tr: this.perspXY(x1, y1),
    };
  }

  /** Check if canvas point is inside a zone (trapezoid hit test) */
  getZoneFromPoint(x: number, y: number): Zone | null {
    const court = this.canvasToCourt(x, y);
    if (!court) return null;
    const { rx, ry } = court;

    // Must be on opponent's half
    if (ry < 0.5) return null;

    // Column
    let col: string;
    if (rx < 0.33) col = 'cross';
    else if (rx < 0.67) col = 'middle';
    else col = 'line';

    // Row
    let row: string;
    if (ry < 0.72) row = 'short';
    else row = 'deep';

    return `${col}-${row}` as Zone;
  }

  /** Convert page coordinates to canvas coordinates */
  pageToCanvas(pageX: number, pageY: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: (pageX - rect.left) * (this.canvas.width / rect.width),
      y: (pageY - rect.top) * (this.canvas.height / rect.height),
    };
  }

  // ---- Drawing helpers ----

  private drawTrapezoid(tl: {x:number,y:number}, tr: {x:number,y:number}, br: {x:number,y:number}, bl: {x:number,y:number}) {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(tl.x, tl.y);
    ctx.lineTo(tr.x, tr.y);
    ctx.lineTo(br.x, br.y);
    ctx.lineTo(bl.x, bl.y);
    ctx.closePath();
  }

  private perspLine(rx0: number, ry0: number, rx1: number, ry1: number) {
    const a = this.perspXY(rx0, ry0);
    const b = this.perspXY(rx1, ry1);
    this.ctx.moveTo(a.x, a.y);
    this.ctx.lineTo(b.x, b.y);
  }

  // ---- Opponent position ----

  private getOpponentXY(pos: OpponentPosition): { x: number; y: number } {
    const positions: Record<OpponentPosition, [number, number]> = {
      'baseline-center':     [0.50, 0.97],
      'baseline-deuce':      [0.30, 0.97],
      'baseline-ad':         [0.70, 0.97],
      'baseline-wide-deuce': [0.10, 0.97],
      'baseline-wide-ad':    [0.90, 0.97],
      'net-center':          [0.50, 0.65],
      'net-deuce':           [0.30, 0.65],
      'net-ad':              [0.70, 0.65],
    };
    const [rx, ry] = positions[pos];
    return this.perspXY(rx, ry);
  }

  // ---- Main render ----

  render(state: GameState, timerPct: number) {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.w, this.h);
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, this.w, this.h);

    if (state.phase === 'menu') {
      this.drawMenu(state);
      return;
    }

    this.drawCourt();

    if (state.phase === 'playing' && state.currentScenario) {
      this.drawZones(state);
      this.drawScenario(state.currentScenario);
      this.drawTimer(timerPct);
    }

    if ((state.phase === 'wrong' || state.phase === 'timeout') && state.currentScenario) {
      this.drawZones(state);
      this.drawScenario(state.currentScenario);
      this.drawResult(state);
    }

    this.drawHUD(state);
  }

  // ---- Court ----

  private drawCourt() {
    const ctx = this.ctx;

    // Court surface — full trapezoid
    const bl = this.perspXY(0, 0);
    const br = this.perspXY(1, 0);
    const tr = this.perspXY(1, 1);
    const tl = this.perspXY(0, 1);
    this.drawTrapezoid(tl, tr, br, bl);
    ctx.fillStyle = '#2d5a27';
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;

    // Outer boundary
    this.drawTrapezoid(tl, tr, br, bl);
    ctx.stroke();

    // Singles sidelines (inset 12.5% each side)
    const si = 0.125;
    ctx.beginPath();
    this.perspLine(si, 0, si, 1);
    this.perspLine(1 - si, 0, 1 - si, 1);
    ctx.stroke();

    // Net at ry=0.5
    ctx.lineWidth = 3;
    ctx.beginPath();
    this.perspLine(0, 0.5, 1, 0.5);
    ctx.stroke();

    // Service lines
    ctx.lineWidth = 2;
    ctx.beginPath();
    // Near service line (player's side, ry ≈ 0.28)
    this.perspLine(si, 0.22, 1 - si, 0.22);
    // Far service line (opponent's side, ry ≈ 0.78)
    this.perspLine(si, 0.78, 1 - si, 0.78);
    ctx.stroke();

    // Center service lines
    ctx.beginPath();
    this.perspLine(0.5, 0.22, 0.5, 0.5);
    this.perspLine(0.5, 0.5, 0.5, 0.78);
    ctx.stroke();

    // Center baseline marks
    const markDepth = 0.02;
    ctx.beginPath();
    this.perspLine(0.5, 0, 0.5, markDepth);
    this.perspLine(0.5, 1 - markDepth, 0.5, 1);
    ctx.stroke();
  }

  // ---- Zones ----

  private drawZones(state: GameState) {
    const ctx = this.ctx;
    const zones: Zone[] = ['cross-deep', 'cross-short', 'line-deep', 'line-short', 'middle-deep', 'middle-short'];

    for (const zone of zones) {
      const corners = this.getZoneCorners(zone);
      const isCorrect = state.currentScenario?.correctReturn === zone;
      const isSelected = state.selectedZone === zone;
      const isGameOver = state.phase === 'wrong' || state.phase === 'timeout';

      // Fill
      if (isGameOver) {
        if (isCorrect) {
          this.drawTrapezoid(corners.tl, corners.tr, corners.br, corners.bl);
          ctx.fillStyle = 'rgba(78, 204, 163, 0.5)';
          ctx.fill();
        }
        if (isSelected && !isCorrect) {
          this.drawTrapezoid(corners.tl, corners.tr, corners.br, corners.bl);
          ctx.fillStyle = 'rgba(255, 71, 87, 0.5)';
          ctx.fill();
        }
      } else {
        this.drawTrapezoid(corners.tl, corners.tr, corners.br, corners.bl);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.04)';
        ctx.fill();
      }

      // Dashed border
      this.drawTrapezoid(corners.tl, corners.tr, corners.br, corners.bl);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Label
      const midX = (corners.tl.x + corners.tr.x + corners.bl.x + corners.br.x) / 4;
      const midY = (corners.tl.y + corners.tr.y + corners.bl.y + corners.br.y) / 4;
      const [dir, depth] = zone.split('-');
      const fontSize = Math.min(this.nearHalfW * 0.08, 13);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.font = `${fontSize}px -apple-system, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(dir, midX, midY - fontSize * 0.6);
      ctx.fillText(depth, midX, midY + fontSize * 0.6);
    }
  }

  // ---- Scenario (opponent + ball) ----

  private drawScenario(scenario: Scenario) {
    const ctx = this.ctx;
    const radius = Math.min(this.nearHalfW * 0.06, 16);

    // Opponent
    const opp = this.getOpponentXY(scenario.opponentPos);
    ctx.fillStyle = '#ff4757';
    ctx.beginPath();
    ctx.arc(opp.x, opp.y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Label "OPP" inside
    ctx.fillStyle = '#fff';
    ctx.font = `bold ${radius * 0.7}px -apple-system, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('OPP', opp.x, opp.y);

    // Ball incoming — figure out where the ball is heading on player's side
    const incomingTarget = this.getIncomingBallTarget(scenario);

    // Player position (near the incoming ball on their side)
    const playerPos = this.perspXY(incomingTarget.rx, 0.05);
    ctx.fillStyle = '#4ecca3';
    ctx.beginPath();
    ctx.arc(playerPos.x, playerPos.y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.fillText('YOU', playerPos.x, playerPos.y);

    // Ball trajectory line
    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(opp.x, opp.y);
    ctx.lineTo(playerPos.x, playerPos.y);
    ctx.stroke();
    ctx.setLineDash([]);

    // Ball
    const ballProgress = 0.65;
    const ballX = opp.x + (playerPos.x - opp.x) * ballProgress;
    const ballY = opp.y + (playerPos.y - opp.y) * ballProgress;
    const ballR = Math.min(this.nearHalfW * 0.035, 10);
    ctx.fillStyle = '#ffd700';
    ctx.beginPath();
    ctx.arc(ballX, ballY, ballR, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Ball depth indicator text
    const depthLabel = scenario.ballDepth === 'short' ? 'SHORT BALL!' : 'DEEP';
    const depthColor = scenario.ballDepth === 'short' ? '#ffa502' : '#888';
    ctx.fillStyle = depthColor;
    const labelSize = Math.min(this.w * 0.04, 16);
    ctx.font = `bold ${labelSize}px -apple-system, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(depthLabel, this.cx, this.nearY + 8);
  }

  /** Where the incoming ball arrives on the player's side (normalized rx) */
  private getIncomingBallTarget(scenario: Scenario): { rx: number; ry: number } {
    const [dir, depth] = scenario.ballDirection.split('-');
    const xMap: Record<string, number> = { 'cross': 0.20, 'middle': 0.50, 'line': 0.80 };
    const yMap: Record<string, number> = { 'deep': 0.08, 'short': 0.30 };
    return { rx: xMap[dir] ?? 0.5, ry: yMap[depth] ?? 0.1 };
  }

  // ---- Timer ----

  private drawTimer(pct: number) {
    const ctx = this.ctx;
    const barW = this.w * 0.8;
    const barH = 6;
    const barX = (this.w - barW) / 2;
    const barY = this.h * 0.015;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.fillRect(barX, barY, barW, barH);

    const r = Math.floor(255 * (1 - pct));
    const g = Math.floor(200 * pct);
    ctx.fillStyle = `rgb(${r}, ${g}, 50)`;
    ctx.fillRect(barX, barY, barW * pct, barH);
  }

  // ---- HUD ----

  private drawHUD(state: GameState) {
    const ctx = this.ctx;

    ctx.fillStyle = '#4ecca3';
    ctx.font = `bold ${Math.min(this.w * 0.06, 28)}px -apple-system, system-ui, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`${state.score}`, this.w * 0.05, this.h * 0.03);

    if (state.streak > 1) {
      ctx.fillStyle = '#ffd700';
      ctx.font = `bold ${Math.min(this.w * 0.04, 18)}px -apple-system, system-ui, sans-serif`;
      ctx.fillText(`${state.streak}x`, this.w * 0.05, this.h * 0.03 + 30);
    }

    if (state.roundBonus > 0 && state.phase === 'playing') {
      ctx.fillStyle = '#ffd700';
      ctx.font = `bold ${Math.min(this.w * 0.05, 22)}px -apple-system, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`+${state.roundBonus}`, this.w / 2, this.h * 0.03);
    }

    ctx.fillStyle = '#888';
    ctx.font = `${Math.min(this.w * 0.035, 14)}px -apple-system, system-ui, sans-serif`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillText(`Best: ${state.bestScore}`, this.w * 0.95, this.h * 0.03);
  }

  // ---- Menu ----

  private drawMenu(state: GameState) {
    const ctx = this.ctx;
    const cx = this.w / 2;
    const cy = this.h / 2;

    ctx.fillStyle = '#4ecca3';
    ctx.font = `bold ${Math.min(this.w * 0.1, 48)}px -apple-system, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('REFLEX', cx, cy - this.h * 0.18);

    ctx.fillStyle = '#e0e0e0';
    ctx.font = `bold ${Math.min(this.w * 0.08, 36)}px -apple-system, system-ui, sans-serif`;
    ctx.fillText('TENNIS', cx, cy - this.h * 0.10);

    ctx.fillStyle = '#888';
    const instrSize = Math.min(this.w * 0.038, 17);
    ctx.font = `${instrSize}px -apple-system, system-ui, sans-serif`;
    ctx.fillText('Read the court. Tap where to hit.', cx, cy + this.h * 0.02);
    ctx.fillText('Cross-court is safe. Attack short balls.', cx, cy + this.h * 0.06);
    ctx.fillText('Wrong answer = game over.', cx, cy + this.h * 0.10);

    if (state.bestScore > 0) {
      ctx.fillStyle = '#4ecca3';
      ctx.font = `bold ${Math.min(this.w * 0.045, 20)}px -apple-system, system-ui, sans-serif`;
      ctx.fillText(`Best: ${state.bestScore}`, cx, cy + this.h * 0.18);
    }

    const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 500);
    ctx.fillStyle = `rgba(78, 204, 163, ${0.5 + pulse * 0.5})`;
    ctx.font = `bold ${Math.min(this.w * 0.055, 24)}px -apple-system, system-ui, sans-serif`;
    ctx.fillText('TAP TO START', cx, cy + this.h * 0.28);
  }

  // ---- Game Over ----

  private drawResult(state: GameState) {
    const ctx = this.ctx;
    const cx = this.w / 2;

    // Semi-transparent overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.fillRect(0, 0, this.w, this.h);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Tactic explanation
    if (state.currentScenario?.tactic) {
      ctx.fillStyle = '#ffd700';
      const tacticSize = Math.min(this.w * 0.035, 15);
      ctx.font = `${tacticSize}px -apple-system, system-ui, sans-serif`;
      // Word wrap the tactic text
      const words = state.currentScenario.tactic.split(' ');
      let line = '';
      let lineY = this.h * 0.78;
      for (const word of words) {
        const test = line ? `${line} ${word}` : word;
        if (ctx.measureText(test).width > this.w * 0.85) {
          ctx.fillText(line, cx, lineY);
          line = word;
          lineY += tacticSize * 1.4;
        } else {
          line = test;
        }
      }
      if (line) ctx.fillText(line, cx, lineY);
    }

    if (state.phase === 'wrong') {
      ctx.fillStyle = '#ff4757';
      ctx.font = `bold ${Math.min(this.w * 0.08, 36)}px -apple-system, system-ui, sans-serif`;
      ctx.fillText('WRONG', cx, this.h * 0.87);
    } else {
      ctx.fillStyle = '#ffa502';
      ctx.font = `bold ${Math.min(this.w * 0.08, 36)}px -apple-system, system-ui, sans-serif`;
      ctx.fillText('TOO SLOW', cx, this.h * 0.87);
    }

    ctx.fillStyle = '#e0e0e0';
    ctx.font = `${Math.min(this.w * 0.05, 22)}px -apple-system, system-ui, sans-serif`;
    ctx.fillText(`Score: ${state.score}`, cx, this.h * 0.92);

    const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 400);
    ctx.fillStyle = `rgba(255, 255, 255, ${0.4 + pulse * 0.4})`;
    ctx.font = `${Math.min(this.w * 0.04, 18)}px -apple-system, system-ui, sans-serif`;
    ctx.fillText('Tap to try again', cx, this.h * 0.97);
  }
}
